/**
 * LanguageWrapper - 言語プレフィックスルーティング対応コンポーネント
 *
 * @file LanguageWrapper.tsx
 * @version 1.0.0
 * @author Japan4U Development Team
 * @summary
 * URLパスの言語プレフィックス（/en, /ko, /ja）から言語を検出し、
 * i18nextの言語設定を自動的に切り替えるラッパーコンポーネント。
 */

import React, { useEffect, useRef } from 'react';
import { Outlet, useParams, useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '@shared/contexts/LanguageContext';
import { SUPPORTED_LANGUAGE_CODES } from '@shared/i18n';

/**
 * サポートされている言語コード
 */
type SupportedLanguage = typeof SUPPORTED_LANGUAGE_CODES[number];

/**
 * 言語コードの検証
 */
const isValidLanguage = (lang: string | undefined): lang is SupportedLanguage => {
  if (!lang) return false;
  return SUPPORTED_LANGUAGE_CODES.includes(lang as SupportedLanguage);
};

/**
 * URLパスから言語プレフィックスを除去し、正規化されたパスを返す
 * 例: /en/cart → /cart, /ko/products → /products, /cart → /cart
 */
const normalizePath = (path: string): string => {
  const langPrefixPattern = /^\/(ja|ko|en|zh)(\/|$)/;
  return path.replace(langPrefixPattern, '/');
};

/**
 * LanguageWrapper
 *
 * URLの言語プレフィックスに基づいてi18n言語を同期し、
 * 子ルートをOutletでレンダリングするラッパーコンポーネント。
 * 言語プレフィックス付きURLは入口専用とし、言語切替後は
 * プレフィックスを除去したURLへリダイレクトする。
 *
 * 使用例:
 * <Route path="/:lang?" element={<LanguageWrapper />}>
 *   <Route element={<Layout />}>
 *     <Route path="products" element={<ProductsPage />} />
 *   </Route>
 * </Route>
 */
export const LanguageWrapper: React.FC = () => {
  const { lang } = useParams<{ lang?: string }>();
  const { currentLanguage, changeLanguage } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();

  // currentLanguageの最新値をrefで参照する。
  // 依存配列にcurrentLanguageを含めると、ユーザーが言語セレクタ等で
  // 明示的に言語を変更した直後にもこのeffectが再実行され、
  // URLの言語プレフィックス（まだnavigateで除去されていない状態）を
  // 根拠に元の言語へ強制的に巻き戻してしまう競合が発生するため、
  // ここでは値の参照のみに留め、effectの再実行トリガーには使わない。
  const currentLanguageRef = useRef(currentLanguage);
  useEffect(() => {
    currentLanguageRef.current = currentLanguage;
  }, [currentLanguage]);

  useEffect(() => {
    // 有効な言語プレフィックスが存在する場合、言語を切り替えたうえで
    // プレフィックスを除去したURLへリダイレクトする
    if (isValidLanguage(lang)) {
      (async () => {
        if (lang !== currentLanguageRef.current) {
          await changeLanguage(lang);
        }
        const normalizedPath = normalizePath(location.pathname);
        navigate(`${normalizedPath}${location.search}${location.hash}`, { replace: true });
      })();
    }
    // 言語プレフィックスがない場合（/products等）は現在の言語を維持
  }, [lang, changeLanguage, location.pathname, location.search, location.hash, navigate]);

  return <Outlet />;
};

export default LanguageWrapper;
